<div class="flex flex-col gap-8">
    <!-- Screening questions -->
    <bb-field label="Screening questions">
        <div class="flex flex-col gap-3">
            @for (question of service.screeningQuestions(); track question.id; let i = $index) {
            <div class="flex items-start gap-4 rounded-lg border border-border bg-card p-[18px] shadow-sm">
                <!-- Index -->
                <div class="w-[26px] shrink-0 pt-0.5 text-center font-heading text-lg font-black text-brand-studio">
                    {{ i + 1 }}
                </div>

                <!-- Body -->
                <div class="min-w-0 flex-1">
                    <!-- Header -->
                    <div class="mb-3 flex items-start justify-between gap-3">
                        <div>
                            <div class="font-heading text-lg font-bold tracking-tight text-foreground">
                                Question {{ i + 1 }}
                                <span class="ml-1 font-semibold text-muted-foreground">· {{ typeLabel(question.type)
                                    }}</span>
                            </div>
                            <div class="mt-0.5 text-xs text-muted-foreground">{{ typeHint(question.type) }}</div>
                        </div>
                        <button type="button"
                            class="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-sm font-semibold text-foreground transition-colors hover:border-brand-studio hover:text-brand-studio"
                            (click)="removeQuestion(i)">Remove</button>
                    </div>

                    <!-- Question text -->
                    <input [value]="question.question" (input)="updateQuestion(i, questionInputValue($event))"
                        (blur)="onQuestionBlur(i)" placeholder="Ask creators a screening question…"
                        class="mb-3.5 w-full rounded-md border border-border bg-card px-3 py-2 text-sm outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25" />

                    <!-- Answer type -->
                    <div class="flex flex-wrap items-center gap-3">
                        <span class="text-xs font-bold uppercase tracking-wider text-muted-foreground">Answer
                            type</span>
                        <bb-segmented [options]="answerTypeOptions" [value]="question.type"
                            (valueChange)="updateQuestionType(i, $event)" />
                    </div>

                    <hr class="my-4 border-border" />

                    <!-- Expected answer -->
                    @switch (question.type) {
                    @case (TypeEnum.OneLine) {
                    <div class="flex items-center gap-2 text-[13px] text-muted-foreground">
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="shrink-0">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                        </svg>
                        Free-text answers are reviewed manually — no auto-check.
                    </div>
                    }
                    @case (TypeEnum.YesNo) {
                    <div>
                        <div class="mb-2.5 flex items-baseline gap-2">
                            <span class="font-heading text-[15px] font-bold text-foreground">Expected answer</span>
                            <span class="text-xs text-muted-foreground">optional — skip to review manually</span>
                        </div>
                        <bb-segmented [options]="yesNoOptions" [value]="editFor(question.id).yesNo ?? ''"
                            (valueChange)="setYesNo(i, question.id, $event)" />
                        @if (passText(question.type, question.id); as pill) {
                        <div
                            class="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-studio/10 px-3 py-1.5 text-[12.5px] font-semibold text-brand-studio-dark dark:text-brand-studio">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                stroke-linejoin="round" class="shrink-0">
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            {{ pill }}
                        </div>
                        }
                    </div>
                    }
                    @case (TypeEnum.Numerical) {
                    <div>
                        <div class="mb-2.5 flex items-baseline gap-2">
                            <span class="font-heading text-[15px] font-bold text-foreground">Expected answer</span>
                            <span class="text-xs text-muted-foreground">optional — skip to review manually</span>
                        </div>
                        <div class="flex flex-wrap items-center gap-3">
                            <span class="text-[15px] font-semibold text-foreground/80">Applicant's answer must be</span>
                            <bb-segmented [options]="operatorOptions" [value]="editFor(question.id).operator"
                                (valueChange)="setOperator(i, question.id, $event)" />
                            <input type="number" inputmode="numeric" placeholder="e.g. 5"
                                [value]="editFor(question.id).valueRaw"
                                (input)="setNumber(i, question.id, $any($event.target).value)"
                                class="max-w-[130px] rounded-md border border-border bg-card px-3 py-2 text-sm outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25" />
                        </div>
                        @if (passText(question.type, question.id); as pill) {
                        <div
                            class="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-studio/10 px-3 py-1.5 text-[12.5px] font-semibold text-brand-studio-dark dark:text-brand-studio">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                stroke-linejoin="round" class="shrink-0">
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            {{ pill }}
                        </div>
                        }
                    </div>
                    }
                    }
                </div>
            </div>
            }
            @if (service.screeningQuestions().length < 3) {
            <div class="flex w-full flex-col items-start gap-3 py-4">
                <p class="text-xs leading-snug text-muted-foreground">Qualify creators before they're matched. Up to 3.</p>
                <button type="button"
                    class="inline-flex items-center gap-2 self-start rounded-md border border-border px-3.5 py-2 text-sm font-semibold text-foreground transition-colors hover:border-brand-studio hover:text-brand-studio"
                    (click)="addQuestion()">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M5 12h14" />
                        <path d="M12 5v14" />
                    </svg>
                    Add question
                </button>
            </div>
            }
</div>
</bb-field>

<hr class="border-border" />

<!-- Compliance documents -->
<bb-field label="Compliance documents">
    <div class="flex flex-col gap-4 pt-1">
        @if (!attachedDocs().length) {
        <div
            class="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-5 py-5 text-sm text-muted-foreground">
            No compliance documents attached yet.
        </div>
        }
        @for (doc of attachedDocs(); track doc.id) {
        <div class="flex items-start gap-3 rounded-lg border border-border bg-card p-4">
            <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                    <span class="font-heading text-base font-bold">{{ doc.complianceName }}</span>
                    <span class="rounded-full px-2 py-0.5 text-[11px] font-bold"
                        [class]="isLinked(doc) ? 'bg-muted text-muted-foreground' : 'bg-brand-studio/10 text-brand-studio-dark dark:text-brand-studio'">
                        {{ isLinked(doc) ? 'Linked doc' : 'Written' }}
                    </span>
                </div>
                @if (doc.subHeading) {
                <div class="mt-0.5 text-xs text-muted-foreground">{{ doc.subHeading }}</div>
                }
            </div>
            <button type="button" aria-label="Remove document"
                class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                (click)="detach(doc.id)">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 6h18" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                </svg>
            </button>
        </div>
        }

        @if (adderMode() === null) {
        <div class="flex flex-wrap gap-2.5 pt-1">
            <button type="button"
                class="inline-flex items-center gap-2 rounded-md border border-border px-3.5 py-2 text-sm font-semibold hover:border-brand-studio hover:text-brand-studio"
                (click)="openLibrary()">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    class="size-4">
                    <path d="m16 6 4 14" />
                    <path d="M12 6v14" />
                    <path d="M8 8v12" />
                    <path d="M4 4v16" />
                </svg>
                Choose from library</button>
            <button type="button"
                class="inline-flex items-center gap-2 rounded-md border border-border px-3.5 py-2 text-sm font-semibold hover:border-brand-studio hover:text-brand-studio"
                (click)="adderMode.set('new')">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    class="size-4">
                    <path d="M5 12h14" />
                    <path d="M12 5v14" />
                </svg>
                Create new document</button>
        </div>
        } @else if (adderMode() === 'library') {
        <div class="rounded-lg border border-border bg-muted/30 p-4">
            <div class="mb-3 flex items-center justify-between">
                <span class="font-heading text-base font-bold">Compliance library</span>
                <button type="button" class="text-sm text-muted-foreground hover:text-foreground"
                    (click)="adderMode.set(null)">Cancel</button>
            </div>
            @if (!availableDocs().length) {
            <p class="text-sm text-muted-foreground">Every library document is already attached.</p>
            }
            <div class="flex flex-col gap-2">
                @for (doc of availableDocs(); track doc.id) {
                <button type="button"
                    class="flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-left hover:border-brand-studio"
                    (click)="attach(doc.id)">
                    <div class="min-w-0 flex-1">
                        <div class="font-heading text-[15px] font-bold">{{ doc.complianceName }}</div>
                        <div class="truncate text-xs text-muted-foreground">{{ doc.description || doc.subHeading }}
                        </div>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="text-muted-foreground">
                        <path d="M5 12h14" />
                        <path d="M12 5v14" />
                    </svg>
                </button>
                }
            </div>
        </div>
        } @else {
        <div class="rounded-lg border border-border bg-card p-4">
            <div class="mb-3 flex items-center justify-between">
                <span class="font-heading text-[17px] font-bold">New compliance document</span>
                <button type="button" class="text-sm text-muted-foreground hover:text-foreground"
                    (click)="cancelNew()">Cancel</button>
            </div>
            <div class="flex flex-col gap-4">
                <bb-field label="Document name" [required]="true">
                    <input [value]="draft().complianceName"
                        (input)="setDraft('complianceName', $any($event.target).value)"
                        placeholder="e.g. FTC Disclosure Guidelines"
                        class="w-full rounded-md border border-border bg-card px-3.5 py-2.5 text-base outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25" />
                </bb-field>
                <bb-field label="Sub-heading">
                    <input [value]="draft().subHeading" (input)="setDraft('subHeading', $any($event.target).value)"
                        placeholder="e.g. Required for all US-based creators"
                        class="w-full rounded-md border border-border bg-card px-3.5 py-2.5 text-base outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25" />
                </bb-field>
                <bb-field label="Description">
                    <textarea [value]="draft().description" (input)="setDraft('description', $any($event.target).value)"
                        rows="2" placeholder="Briefly describe what this document covers."
                        class="w-full resize-y rounded-md border border-border bg-card px-3.5 py-2.5 text-base outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25"></textarea>
                </bb-field>
                <bb-field label="How is the content provided?">
                    <bb-segmented [options]="contentModeOptions" [value]="contentMode()"
                        (valueChange)="contentMode.set($any($event))" />
                </bb-field>
                @if (contentMode() === 'write') {
                <bb-field label="Document content">
                    <textarea [value]="draft().complianceContent"
                        (input)="setDraft('complianceContent', $any($event.target).value)" rows="5"
                        placeholder="Write the compliance terms creators must read and acknowledge…"
                        class="w-full resize-y rounded-md border border-border bg-card px-3.5 py-2.5 text-base outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25"></textarea>
                </bb-field>
                } @else {
                <bb-field label="Document link">
                    <input [value]="draft().complianceDocumentUrl"
                        (input)="setDraft('complianceDocumentUrl', $any($event.target).value)"
                        placeholder="https://docs.google.com/… or a link to a PDF"
                        class="w-full rounded-md border border-border bg-card px-3.5 py-2.5 text-base outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25" />
                </bb-field>
                }
                <button type="button" class="flashy-go-button self-start disabled:opacity-50"
                    [disabled]="!draft().complianceName.trim() || saving()" (click)="saveNew()">
                    {{ saving() ? 'Saving…' : 'Save document' }}
                </button>
            </div>
        </div>
        }

        <p class="pt-1 text-xs leading-snug text-muted-foreground">
            Creators must acknowledge these before anything goes live.
        </p>
    </div>
</bb-field>

</div>